import { useEffect, useRef, useState } from "react";
import { ExternalLink, Loader2, Save, Trash2, Zap, AlertTriangle, PlayCircle } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { usePoll } from "@/hooks/usePoll";
import { api, admin, errMsg } from "@/lib/api";
import { fmtDateTime } from "@/lib/format";
import { StatusBadge } from "@/components/bridge/StatusBadge";
import { TokenCountdown } from "@/components/bridge/TokenCountdown";
import { SecretField } from "./SecretField";

export const ZerodhaTab = ({ pw }) => {
  const { data, refresh } = usePoll(() => api.get("/admin/zerodha", admin(pw)).then((r) => r.data), 5000, [pw]);
  const [form, setForm] = useState({ api_key: "", api_secret: "" });
  const [busy, setBusy] = useState("");

  const run = async (name, fn) => {
    setBusy(name);
    try {
      await fn();
      refresh();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy("");
    }
  };
  const save = () => run("save", async () => {
    await api.put("/admin/zerodha", form, admin(pw));
    setForm({ api_key: "", api_secret: "" });
    toast.success("Zerodha keys saved. Now log in with Zerodha.");
  });
  const login = () => run("login", async () => {
    const { data: d } = await api.get("/admin/zerodha/login-url", admin(pw));
    window.location.href = d.login_url;
  });
  const clear = () => run("clear", async () => {
    await api.delete("/admin/zerodha", admin(pw));
    toast.success("Keys removed. Simulated feed active.");
  });

  const st = data?.status;
  return (
    <div className="space-y-5 pt-3" data-testid="zerodha-tab">
      <div className="flex flex-wrap items-center gap-2 rounded border border-slate-800 bg-slate-950/40 p-3 text-xs">
        <StatusBadge status={st?.state} testid="zerodha-state-badge" />
        {st?.user_id && <span className="font-mono text-slate-300" data-testid="zerodha-user">{st.user_id} · {st.user_name}</span>}
        {st?.login_time && <span className="font-mono text-slate-500">login {fmtDateTime(st.login_time)}</span>}
        {st?.reason && <span className="w-full text-slate-400">{st.reason}</span>}
      </div>

      {st?.expected_expiry && (
        <div className="rounded border border-slate-800 bg-slate-950/40 p-3" data-testid="zerodha-countdown-box">
          <TokenCountdown expiry={st.expected_expiry} testid="zerodha-token-countdown" bar />
          <div className="mt-1.5 font-mono text-[10px] text-slate-500">
            Resets ~{fmtDateTime(st.expected_expiry)} · last checked {fmtDateTime(st.last_validated)}
          </div>
        </div>
      )}

      <div className="grid gap-3 sm:grid-cols-2">
        <SecretField label="Current API key" value={data?.api_key} testid="zerodha-current-api-key" />
        <SecretField label="Current API secret" value={data?.api_secret} testid="zerodha-current-api-secret" />
      </div>
      <SecretField label="Redirect URL (register this in your Kite developer app)" value={data?.redirect_url} testid="zerodha-redirect-url" />

      <div className="space-y-2 rounded border border-slate-800 p-3">
        <div className="text-xs font-semibold uppercase tracking-wider text-slate-400">{data?.api_key ? "Change keys" : "Add keys"}</div>
        <div className="grid gap-2 sm:grid-cols-2">
          <Input data-testid="zerodha-api-key-input" placeholder="Kite API key" value={form.api_key} onChange={(e) => setForm({ ...form, api_key: e.target.value })}
            className="border-slate-800 bg-slate-900 font-mono" />
          <Input data-testid="zerodha-api-secret-input" type="password" placeholder="Kite API secret" value={form.api_secret}
            onChange={(e) => setForm({ ...form, api_secret: e.target.value })} className="border-slate-800 bg-slate-900 font-mono" />
        </div>
        <Button data-testid="zerodha-save-button" onClick={save} disabled={busy || form.api_key.length < 4 || form.api_secret.length < 4}
          className="bg-cyan-500 font-semibold text-slate-950 hover:bg-cyan-400">
          {busy === "save" ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="mr-1.5 h-4 w-4" /> Save keys</>}
        </Button>
        <p className="text-[11px] text-slate-500">Saving new keys ends the current Kite session. Secrets are encrypted at rest.</p>
      </div>

      <div className="flex flex-wrap gap-2">
        <Button data-testid="zerodha-login-button" onClick={login} disabled={!st?.configured || !!busy} className="bg-emerald-500 font-semibold text-slate-950 hover:bg-emerald-400">
          {busy === "login" ? <Loader2 className="h-4 w-4 animate-spin" /> : <><ExternalLink className="mr-1.5 h-4 w-4" /> Login with Zerodha</>}
        </Button>
        {st?.configured && (
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button data-testid="zerodha-clear-button" variant="outline" className="border-rose-900 bg-transparent text-rose-400 hover:bg-rose-950/40">
                <Trash2 className="mr-1.5 h-4 w-4" /> Remove keys
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent className="border-slate-800 bg-[#0E1420]">
              <AlertDialogHeader>
                <AlertDialogTitle>Remove Zerodha keys?</AlertDialogTitle>
                <AlertDialogDescription>The Kite session stops and the service switches back to the simulated feed.</AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel data-testid="zerodha-clear-cancel">Cancel</AlertDialogCancel>
                <AlertDialogAction data-testid="zerodha-clear-confirm" onClick={clear} className="bg-rose-600 hover:bg-rose-500">Remove</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        )}
      </div>
      <p className="text-[11px] leading-relaxed text-slate-500">
        Kite access tokens reset daily (~06:00 IST) and need one interactive login (with your 2FA) per day. The service reuses the session while
        valid and raises an <span className="text-rose-400">AUTH ISSUE</span> banner the moment Zerodha requires a fresh login.
      </p>

      <AutoLoginCard pw={pw} status={st} saved={data} refresh={refresh} />
    </div>
  );
};

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const DayPicker = ({ value, onChange }) => (
  <div className="flex flex-wrap items-center gap-1.5" data-testid="autologin-days">
    <span className="mr-1 text-[10px] uppercase tracking-wider text-slate-500">Run on</span>
    {DAYS.map((d, i) => {
      const on = value.includes(i);
      return (
        <button key={d} type="button" data-testid={`autologin-day-${d.toLowerCase()}`} aria-pressed={on}
          onClick={() => onChange(on ? value.filter((x) => x !== i) : [...value, i].sort())}
          className={`rounded px-2.5 py-1 font-mono text-[11px] transition-colors ${on ? "bg-amber-500 text-slate-950" : "border border-slate-800 bg-slate-900 text-slate-500 hover:text-slate-300"}`}>
          {d}
        </button>
      );
    })}
  </div>
);

const TODAY_TEXT = {
  disabled: () => "Auto-login is off — no run scheduled",
  off_day: () => "Not scheduled today (day is off)",
  pending: () => "Today's time will be picked once credentials are saved",
  window_passed: () => "Today's window has passed — next run on the next selected day",
  scheduled: (at) => `Today's run: ${at} IST`,
  done: (at) => `Today's run: ${at} IST (done)`,
};

const TodayRun = ({ today }) => today ? (
  <span data-testid="autologin-today-run" className="font-mono text-[11px] text-amber-300">
    {TODAY_TEXT[today.state]?.(today.at)}
  </span>
) : null;

const AutoLoginCard = ({ pw, status, saved, refresh }) => {
  const [f, setF] = useState({ autologin_user_id: "", autologin_password: "", autologin_totp_secret: "",
    autologin_window_from: "08:45", autologin_window_to: "08:45", autologin_days: [0, 1, 2, 3, 4] });
  const [busy, setBusy] = useState("");
  const touched = useRef(false);
  const ready = status?.autologin_ready;
  // prefill from the saved settings until the user starts editing, so saving never resets the window/days
  useEffect(() => {
    if (touched.current || !saved) return;
    setF((p) => ({ ...p, autologin_user_id: saved.autologin_user_id || "",
      autologin_window_from: saved.autologin_window_from || "08:45", autologin_window_to: saved.autologin_window_to || "08:45",
      autologin_days: saved.autologin_days || [0, 1, 2, 3, 4] }));
  }, [saved?.autologin_user_id, saved?.autologin_window_from, saved?.autologin_window_to, (saved?.autologin_days || []).join()]); // eslint-disable-line react-hooks/exhaustive-deps
  const edit = (k, v) => { touched.current = true; setF((p) => ({ ...p, [k]: v })); };
  const on = status?.autologin_enabled;

  const run = async (name, fn) => {
    setBusy(name);
    try {
      await fn();
      refresh();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy("");
    }
  };
  const toggle = (v) => run("toggle", async () => {
    await api.put("/admin/zerodha/autologin", { autologin_enabled: v }, admin(pw));
    toast.success(v ? "Auto-login enabled" : "Auto-login disabled");
  });
  const save = () => run("save", async () => {
    const body = Object.fromEntries(Object.entries(f).filter(([, v]) => v !== ""));
    await api.put("/admin/zerodha/autologin", body, admin(pw));
    touched.current = false;
    setF({ ...f, autologin_password: "", autologin_totp_secret: "" });
    toast.success("Auto-login settings saved");
  });
  const [job, setJob] = useState(null);
  const timer = useRef(null);
  useEffect(() => () => clearTimeout(timer.current), []);
  const pollJob = async () => {
    try {
      const { data } = await api.get("/admin/zerodha/autologin/job", admin(pw));
      setJob(data.job);
      if (data.job?.running) { timer.current = setTimeout(pollJob, 1500); return; }
      setBusy("");
      refresh();
      if (data.job?.ok) toast.success("Auto-login succeeded");
      else if (data.job?.ok === false) toast.error(`Auto-login failed: ${data.job.message}`);
    } catch (e) {
      timer.current = setTimeout(pollJob, 3000);
    }
  };
  const now = async () => {
    setBusy("now");
    try {
      const { data } = await api.post("/admin/zerodha/autologin/run", null, admin(pw));
      setJob(data.job);
      timer.current = setTimeout(pollJob, 800);
    } catch (e) {
      setBusy("");
      toast.error(errMsg(e));
    }
  };
  const clear = () => run("clear", async () => {
    await api.delete("/admin/zerodha/autologin", admin(pw));
    toast.success("Auto-login credentials removed");
  });

  return (
    <div className="space-y-3 rounded border border-amber-900/50 bg-amber-950/10 p-3" data-testid="autologin-card">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Zap className="h-4 w-4 text-amber-400" />
          <span className="text-xs font-semibold uppercase tracking-wider text-amber-300">Hands-free auto-login</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="font-mono text-[10px] text-slate-500">{on ? "ENABLED" : "OFF"}</span>
          <Switch data-testid="autologin-enabled-switch" checked={!!on} onCheckedChange={toggle} disabled={!status?.autologin_configured && !on} />
        </div>
      </div>
      <div className="flex gap-2 rounded border border-amber-900/50 bg-amber-950/30 p-2.5 text-[11px] text-amber-200/90">
        <AlertTriangle className="h-4 w-4 shrink-0" />
        <span>
          This logs in as you every morning by replaying your Zerodha password and 2FA code. It is <b>not supported by Zerodha</b>, can break
          without notice and carries account risk. Your password and TOTP secret are stored encrypted and never shown again. If a login fails, the
          service stops, shows AUTH ISSUE and emails you.
        </span>
      </div>
      <div className="grid gap-2 sm:grid-cols-2">
        <Input data-testid="autologin-userid-input" placeholder="Zerodha user ID (e.g. AB1234)" value={f.autologin_user_id}
          onChange={(e) => edit("autologin_user_id", e.target.value)} className="border-slate-800 bg-slate-900 font-mono" />
        <div className="flex items-center gap-2">
          <Input data-testid="autologin-window-from-input" type="time" aria-label="Window start (IST)" value={f.autologin_window_from}
            onChange={(e) => edit("autologin_window_from", e.target.value)} className="border-slate-800 bg-slate-900 font-mono" />
          <span className="text-[11px] text-slate-500">to</span>
          <Input data-testid="autologin-window-to-input" type="time" aria-label="Window end (IST)" value={f.autologin_window_to}
            onChange={(e) => edit("autologin_window_to", e.target.value)} className="border-slate-800 bg-slate-900 font-mono" />
        </div>
        <Input data-testid="autologin-password-input" type="password" placeholder={status?.autologin_configured ? "Password (set — leave blank to keep)" : "Zerodha password"}
          value={f.autologin_password} onChange={(e) => edit("autologin_password", e.target.value)} className="border-slate-800 bg-slate-900 font-mono" />
        <Input data-testid="autologin-totp-input" type="password" placeholder="TOTP secret key (base32)" value={f.autologin_totp_secret}
          onChange={(e) => edit("autologin_totp_secret", e.target.value)} className="border-slate-800 bg-slate-900 font-mono" />
      </div>
      <DayPicker value={f.autologin_days} onChange={(v) => edit("autologin_days", v)} />
      <TodayRun today={status?.autologin_today} />
      <p className="text-[10px] text-slate-500">
        TOTP secret = the key Zerodha showed when you set up your authenticator app (External TOTP). Login window is in IST: each selected day the
        script runs once at a random time inside it (different every day). Set both times the same for a fixed time. Runs on holidays too if the day is selected.
      </p>
      <div className="flex flex-wrap items-center gap-2">
        <Button data-testid="autologin-save-button" onClick={save} disabled={!!busy || !f.autologin_user_id} size="sm"
          className="bg-amber-500 font-semibold text-slate-950 hover:bg-amber-400">
          {busy === "save" ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="mr-1.5 h-4 w-4" /> Save credentials</>}
        </Button>
        <Button data-testid="autologin-run-button" onClick={now} disabled={!!busy || !ready} size="sm" variant="outline"
          className="border-slate-700 bg-slate-900 text-slate-200 hover:bg-slate-800">
          {busy === "now" ? <Loader2 className="h-4 w-4 animate-spin" /> : <><PlayCircle className="mr-1.5 h-4 w-4" /> Try now</>}
        </Button>
        {status?.autologin_configured && (
          <Button data-testid="autologin-clear-button" onClick={clear} disabled={!!busy} size="sm" variant="ghost"
            className="text-rose-400 hover:bg-rose-950/40 hover:text-rose-300">
            <Trash2 className="mr-1.5 h-4 w-4" /> Remove credentials
          </Button>
        )}
        {job && (
          <span data-testid="autologin-job-step" className={`flex items-center gap-1.5 font-mono text-[11px] ${job.ok === false ? "text-rose-400" : job.ok ? "text-emerald-400" : "text-amber-300"}`}>
            {job.running && <Loader2 className="h-3 w-3 animate-spin" />}
            {job.running ? job.step : job.ok ? "Logged in" : `Failed: ${job.message || job.step}`}
          </span>
        )}
        {status?.autologin_last_attempt && (
          <span className="ml-auto font-mono text-[10px] text-slate-500" data-testid="autologin-last-attempt">last run {fmtDateTime(status.autologin_last_attempt)}</span>
        )}
      </div>
    </div>
  );
};
